import { Component, OnInit } from '@angular/core';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';

/**
 * PARÁMETROS → CUENTAS DE ORIGEN (3.4; Mario 08-oct: pantalla antes de subir). De dónde sale el pago a los comercios:
 * cuentas de la empresa pagadora. La gerente las carga, las corrige y las da de baja con motivo; nunca se borran y cada
 * cambio queda en su historia. Las reglas (20 dígitos, banco por prefijo, titular hasta 35, RIF, métodos, BDV solo desde
 * 0102 con número de negociación) las valida el núcleo: aquí se arma el formulario y se muestran sus errores.
 */
@Component({
  selector: 'app-cuentas-origen',
  templateUrl: './cuentas-origen.component.html'
})
export class CuentasOrigenComponent implements OnInit {
  /** INT-04: las integraciones de pago que existen además de MANUAL y BDV_TXT (Parámetros → Integraciones bancarias). */
  otrosMetodos: string[] = [];
  cargando = true;
  error = '';
  detalle: string[] = [];
  aviso = '';
  cuentas: any[] = [];
  personas: { [id: string]: string } = {};
  proveedores: any[] = [];
  bancos: any[] = [];

  editando: string | null = null;          // null: nada; 'nueva'; o el _id de la cuenta
  f: any = this.vacia();
  guardando = false;
  bajando: string | null = null;
  motivoBaja = '';
  historiaAbierta: string | null = null;

  constructor(private liq: LiquidacionService) { }

  ngOnInit(): void {
    this.cargar();
    this.liq.opcionesCuentaOrigen().then((o) => { this.proveedores = o?.proveedores || []; this.bancos = o?.bancos || []; this.otrosMetodos = (o?.metodos || []).filter((m: string) => !['MANUAL', 'BDV_TXT'].includes(m)); }).catch(() => { });
  }

  private vacia(): any {
    return { code: '', provider: '', account_number: '', account_type: 'CORRIENTE', holder_name: '', tax_id: '', currency_code: 'VES', manual: true, bdv: false, otros: {}, negotiation_number: '', notes: '' };
  }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    try { const r = await this.liq.cuentasOrigenConHistoria(); this.cuentas = r?.cuentas || []; this.personas = r?.personas || {}; }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudieron leer las cuentas de origen.'; }
    finally { this.cargando = false; }
  };

  nueva = (): void => { this.editando = 'nueva'; this.f = this.vacia(); this.error = ''; this.detalle = []; this.aviso = ''; };
  editar = (c: any): void => {
    this.editando = c._id; this.error = ''; this.detalle = []; this.aviso = '';
    this.f = { code: c.code, provider: c.provider?._id || c.provider, account_number: c.account_number, account_type: c.account_type, holder_name: c.holder_name,
      tax_id: c.tax_id, currency_code: c.currency_code || 'VES', manual: (c.methods || []).includes('MANUAL'), bdv: (c.methods || []).includes('BDV_TXT'),
      otros: Object.fromEntries((c.methods || []).filter((m: string) => !['MANUAL', 'BDV_TXT'].includes(m)).map((m: string) => [m, true])),
      negotiation_number: c.bdv?.negotiation_number || '', notes: c.notes || '' };
  };
  cancelar = (): void => { this.editando = null; this.error = ''; this.detalle = []; };

  /** El banco sale de los 4 primeros dígitos (como en el núcleo): se muestra mientras se escribe. */
  get bancoDelNumero(): string {
    const d = String(this.f.account_number || '').replace(/\D/g, '');
    if (d.length < 4) return '';
    const b = this.bancos.find((x) => x.bankCode === d.slice(0, 4));
    return b ? b.name : 'ningún banco que pague tiene ese prefijo';
  }

  private datos(): any {
    const methods = [...(this.f.manual ? ['MANUAL'] : []), ...(this.f.bdv ? ['BDV_TXT'] : []), ...this.otrosMetodos.filter((m) => this.f.otros[m])];
    return { code: String(this.f.code || '').trim().toUpperCase(), provider: this.f.provider, account_number: this.f.account_number, account_type: this.f.account_type,
      holder_name: this.f.holder_name, tax_id: this.f.tax_id, currency_code: this.f.currency_code, methods,
      bdv: this.f.bdv ? { negotiation_number: this.f.negotiation_number || null } : undefined, notes: this.f.notes || undefined };
  }

  guardar = async (): Promise<void> => {
    this.guardando = true; this.error = ''; this.detalle = []; this.aviso = '';
    try {
      const r = this.editando === 'nueva' ? await this.liq.crearCuentaOrigen(this.datos()) : await this.liq.cambiarCuentaOrigenDatos(String(this.editando), this.datos());
      this.aviso = `Cuenta ${r?.cuenta?.code || ''} guardada.${(r?.avisos || []).length ? ' ' + r.avisos.join(' ') : ''}`;
      this.editando = null;
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo guardar la cuenta.'; this.detalle = e?.error?.detalle || []; }
    finally { this.guardando = false; }
  };

  darDeBaja = async (c: any): Promise<void> => {
    if (!this.motivoBaja.trim()) { this.error = 'Para dar de baja la cuenta escribe el motivo.'; return; }
    this.guardando = true; this.error = ''; this.detalle = []; this.aviso = '';
    try {
      await this.liq.cambiarCuentaOrigenDatos(c._id, { status: false, motivo: this.motivoBaja.trim() });
      this.aviso = `Cuenta ${c.code} dada de baja: ya no se ofrece al armar lotes. Queda en la lista con su historia.`;
      this.bajando = null; this.motivoBaja = '';
      await this.cargar();
    } catch (e: any) { this.error = e?.error?.msg || 'No se pudo dar de baja.'; }
    finally { this.guardando = false; }
  };

  /** Qué pasó en cada renglón de la historia (la historia guarda la foto de antes y de después). */
  queHizo = (h: any, i: number): string => {
    if (i === 0) return 'cargó la cuenta';
    if (h.antes?.status !== false && h.despues?.status === false) return 'la dio de baja';
    const campos = Object.keys(h.despues || {}).filter((k) => JSON.stringify((h.antes || {})[k]) !== JSON.stringify(h.despues[k]));
    return campos.length ? `corrigió ${campos.join(', ')}` : 'la guardó sin cambios';
  };
  quien = (u: string): string => this.personas[u] || 'otra persona';
  enmascarar = (n: string): string => { const d = String(n || '').replace(/\D/g, ''); return d.length >= 8 ? `${d.slice(0, 4)}-····-${d.slice(-4)}` : d; };
  hora = (f: any): string => {
    const d = new Date(new Date(f).getTime() - 4 * 3600e3);
    return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()} ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  };
  readonly METODO: any = { MANUAL: 'Manual', BDV_TXT: 'TXT de BDV' };
}
